import { toast } from 'sonner';
import i18next from 'i18next';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

import { useDocumentStore } from '@/store/document.store.js';
import { useTenantStore } from '@/store/tenant.store.js';
import { toUploadProgressHandler } from '@/lib/upload/percent-from-axios-progress.js';

export class DocumentIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error) {
        return getIntegrationErrorMessage(error);
    }

    async fetchAll(params = {}) {
        const store = useDocumentStore.getState();
        store.setLoading?.(true);
        store.clearError?.();
        try {
            const response = await this.service.getAll(params);
            return { success: true, data: response.data, meta: response.meta };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            store.setError?.(message);
            return { success: false, error: message };
        } finally {
            store.setLoading?.(false);
        }
    }

    async fetchCatalogReportUsage() {
        try {
            const response = await this.service.fetchCatalogReportUsage();
            return {
                success: response?.success !== false,
                data: {
                    reportKeys: Array.isArray(response?.data?.reportKeys)
                        ? response.data.reportKeys
                        : [],
                },
                message: response?.message,
                error: response?.error,
                code: response?.code,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, data: { reportKeys: [] } };
        }
    }

    async fetchPublicCatalog() {
        try {
            return await this.service.getPublicCatalog();
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error), data: [] };
        }
    }

    async fetchPublicShare(documentId) {
        try {
            return await this.service.getPublicShare(documentId);
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async updatePublicShare(documentId, body) {
        try {
            const response = await this.service.updatePublicShare(documentId, body);
            toast.success(i18next.t(
                body.enabled
                    ? 'reportOperationsDocument:messages.publicShareEnabled'
                    : 'reportOperationsDocument:messages.publicShareDisabled',
                {
                    defaultValue: body.enabled
                        ? 'Public sharing enabled'
                        : 'Public sharing disabled',
                },
            ));
            return response;
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, code: error.code };
        }
    }

    /**
     * Report operations / document list (mapped rows + pagination).
     */
    async fetchReportDocuments(
        page = 1,
        pageSize = 10,
        search = '',
        sortBy = '',
        sortOrder = 'asc',
        columnFilters,
    ) {
        const store = useDocumentStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const effectiveColumnFilters =
                columnFilters !== undefined ? columnFilters : store.columnFilters || {};
            const listExtra = store.documentListQueryExtra || {};
            const response = await this.service.fetchReportDocumentList({
                page,
                pageSize,
                search,
                sortBy,
                sortOrder,
                columnFilters: effectiveColumnFilters,
                extra: listExtra,
            });

            if (response?.success && response.data) {
                store.setReportListState({
                    documents: response.data.documents,
                    uniqueValues: response.data.uniqueValues || {},
                    columnFilters: effectiveColumnFilters,
                    listScope: response.data.scope,
                    paginationData:
                        response.data.pagination || {
                            currentPage: page,
                            totalPages: 1,
                            totalItems: response.data.documents?.length || 0,
                            itemsPerPage: pageSize,
                            hasNextPage: false,
                            hasPreviousPage: false,
                        },
                });
            }
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
        } finally {
            store.setLoading(false);
        }
    }

    async runDocumentReport(documentId, input = {}) {
        try {
            const response = await this.service.runReport(documentId, input);
            if (response?.success === false) {
                return { success: false, error: response.error, code: response.code };
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message, code: error.code };
        }
    }

    async fetchDocumentById(id) {
        const store = useDocumentStore.getState();
        store.setSelectedDocumentLoading(true);
        store.clearError();
        try {
            const response = await this.service.getMappedById(id);
            if (response.success && response.data) {
                store.setSelectedDocument(response.data);
            } else {
                store.setSelectedDocument(null);
            }
            return response.data;
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            store.setSelectedDocument(null);
            toast.error(message);
            return null;
        } finally {
            store.setSelectedDocumentLoading(false);
        }
    }

    async createReportDocument(data, onProgress) {
        const store = useDocumentStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const response = await this.service.createWithOptionalFile(data, {
                onUploadProgress: toUploadProgressHandler(onProgress),
            });
            if (response.success === false || response.error) {
                const message = response.error || i18next.t(
                    'reportOperationsDocument:createDialog.messages.errorCreating',
                    { defaultValue: 'Document could not be created' },
                );
                toast.error(message);
                return {
                    success: false,
                    error: message,
                    code: response.code,
                };
            }
            await this.fetchReportDocuments(
                store.paginationData.currentPage || 1,
                store.paginationData.itemsPerPage || 10,
            );
            store.closeAddDialog();
            return {
                success: true,
                pendingApproval: Boolean(response?.pendingApproval),
                pendingChangeId: response?.pendingChangeId || null,
                data: response?.data,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async associateAssignmentTemplates(documentId, flowIds) {
        const store = useDocumentStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const result = await this.service.associateAssignmentTemplates(documentId, { flowIds });
            if (result?.success === false) {
                return { success: false, error: result.error };
            }
            store.closeApprovalDialog();
            await this.fetchReportDocuments(
                store.paginationData.currentPage,
                store.paginationData.itemsPerPage,
            );
            const refreshed = useDocumentStore.getState().selectedDocument;
            const sid = refreshed?.id || refreshed?._id || refreshed?.ID;
            if (sid && String(sid) === String(documentId)) {
                await this.fetchDocumentById(documentId);
            }
            toast.success(
                i18next.t('reportOperationsDocument:messages.assignmentTemplatesUpdated', {
                        defaultValue: 'Assignment template updated',
                    }),
            );
            return { success: true, data: result?.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(
                message ||
                    i18next.t('reportOperationsDocument:messages.assignmentTemplatesUpdateFailed', {
                        defaultValue: 'Could not update assignment template',
                    }),
            );
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async updateReportDocument(documentId, data) {
        const store = useDocumentStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const result = await this.service.updateDocument(documentId, data);
            if (result?.success === false) {
                return { success: false, error: result.error, code: result.code };
            }
            await this.fetchReportDocuments(
                store.paginationData.currentPage,
                store.paginationData.itemsPerPage,
            );
            const sel = useDocumentStore.getState().selectedDocument;
            const sid = sel?.id || sel?._id || sel?.ID;
            if (sid && String(sid) === String(documentId)) {
                await this.fetchDocumentById(documentId);
            }
            return {
                success: true,
                pendingApproval: Boolean(result?.pendingApproval),
                pendingChangeId: result?.pendingChangeId || null,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            throw error;
        } finally {
            store.setLoading(false);
        }
    }

    async deleteReportDocument(documentId) {
        const store = useDocumentStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const result = await this.service.deleteDocument(documentId);
            if (result?.success === false) {
                return { success: false, error: result.error };
            }
            await this.fetchReportDocuments(
                store.paginationData.currentPage,
                store.paginationData.itemsPerPage,
            );
            if (result?.pendingChangeId || result?.pendingApproval) {
                toast.info(i18next.t('integrationToasts:deleteRequestSubmittedToApprovalWorkflow', { defaultValue: 'Delete request submitted to approval workflow' }));
            } else {
                toast.success(i18next.t('integrationToasts:documentDeleted', { defaultValue: 'Document deleted' }));
            }
            return {
                success: true,
                pendingApproval: Boolean(result?.pendingApproval),
                pendingChangeId: result?.pendingChangeId || null,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async bulkDeleteReportDocuments(documentIds = []) {
        const store = useDocumentStore.getState();
        store.setLoading(true);
        store.clearError();
        try {
            const ids = (documentIds || []).filter(Boolean);
            if (ids.length === 0) {
                return { success: true };
            }
            const failures = [];
            const succeededIds = [];
            const immediateIds = [];
            const pendingIds = [];
            const failedIds = [];
            for (const id of [...new Set(ids.map(String))]) {
                try {
                    const result = await this.service.deleteDocument(id);
                    if (result?.success === false) {
                        failedIds.push(id);
                        failures.push(result.error || 'Delete failed');
                    } else {
                        succeededIds.push(id);
                        if (result?.pendingApproval || result?.pendingChangeId) pendingIds.push(id);
                        else immediateIds.push(id);
                    }
                } catch (error) {
                    failedIds.push(id);
                    failures.push(this._getErrorMessage(error));
                }
            }
            await this.fetchReportDocuments(
                store.paginationData.currentPage,
                store.paginationData.itemsPerPage,
            );
            const pendingCount = pendingIds.length;
            const immediateCount = immediateIds.length;
            if (failures.length > 0) {
                toast.error(
                    i18next.t('reportOperationsDocument:messages.bulkDeletePartialFailure', {
                        count: failures.length,
                        defaultValue: `${failures.length} document(s) could not be deleted`,
                    }),
                );
            }
            return {
                success: failures.length === 0,
                pendingCount,
                immediateCount,
                failedCount: failures.length,
                failedIds,
                succeededIds,
                immediateIds,
                pendingIds,
                error: failures[0],
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async getById(id) {
        try {
            const response = await this.service.getById(id);
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: error.message };
        }
    }

    async create(data) {
        try {
            const response = await this.service.create(data);
            toast.success(i18next.t('integrationToasts:recordCreated', { defaultValue: 'Record created' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: error.message };
        }
    }

    async update(id, data) {
        try {
            const response = await this.service.update(id, data);
            toast.success(i18next.t('integrationToasts:recordUpdated', { defaultValue: 'Record updated' }));
            return { success: true, data: response.data };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: error.message };
        }
    }

    async delete(id) {
        try {
            await this.service.delete(id);
            toast.success(i18next.t('integrationToasts:recordDeleted', { defaultValue: 'Record deleted' }));
            return { success: true };
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { success: false, error: error.message };
        }
    }

    /** Rapor tasarımcısı — designerOnly listesi (DependencySelector). */
    async fetchDesignerDocumentsForSelector(params = {}) {
        try {
            const response = await this.service.fetchReportDocumentList({
                page: params.page || 1,
                pageSize: params.pageSize || 10,
                search: params.search || '',
                sortBy: params.sortBy || '',
                sortOrder: params.sortOrder || 'asc',
                columnFilters: params.columnFilters || {},
                extra: {
                    designerOnly: true,
                    excludeIds: params.excludeIds,
                    relatedVendorId: params.relatedVendorId,
                },
            });
            const docs = response?.data?.documents || [];
            const mappedItems = docs.map((report) => ({
                ...report,
                id: report.ID || report._id,
                name: report.reportName || report.documentName || report.title,
                description: report.description,
                type: report.type,
                author: report.author,
                expiryDate: report.expiryDate,
                location: report.location,
                accessPermissions: report.accessPermissions,
                unit: report.unit,
            }));
            return {
                data: mappedItems,
                total: response?.data?.pagination?.totalItems ?? mappedItems.length,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { data: [], total: 0, error: message };
        }
    }

    /** Genel bağımlılık seçici (designerOnly yok). */
    async fetchDocumentsForDependencySelector(params = {}) {
        try {
            return await this.service.getAllForDependencySelector(params);
        } catch (error) {
            toast.error(this._getErrorMessage(error));
            return { data: [], total: 0 };
        }
    }

    async createReportDesignerCapability(documentId) {
        try {
            const data = await this.service.createReportDesignerCapability(documentId);
            return { success: true, data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /** Tasarımcıda Kaydet sonrası — revizyon oluşturmaz, yalnızca taslak bayrağı. */
    async markDynamicReportDraft(documentId) {
        try {
            await this.service.markDynamicReportDraft(documentId);
            return { success: true };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    /** Mevcut .repx şablonunu doküman taslağına kopyalar ve taslak revizyonu hazırlar. */
    async seedDynamicReportTemplate(documentId, sourceReportName) {
        try {
            const response = await this.service.seedDynamicReportTemplate(documentId, { sourceReportName });
            if (response?.success === false) {
                const message = response.error || i18next.t(
                    'reportOperationsDocument:messages.dynamicTemplateSeedFailed',
                    { defaultValue: 'The existing report template could not be copied into the document' },
                );
                toast.error(message);
                return { success: false, error: message, code: response.code };
            }

            const draftResult = await this.markDynamicReportDraft(documentId);
            if (!draftResult.success) return draftResult;

            const store = useDocumentStore.getState();
            await this.fetchReportDocuments(
                store.paginationData.currentPage || 1,
                store.paginationData.itemsPerPage || 10,
            );
            return { success: true, data: response?.data };
        } catch (error) {
            const message = this._getErrorMessage(error) || i18next.t(
                'reportOperationsDocument:messages.dynamicTemplateSeedFailed',
                { defaultValue: 'The existing report template could not be copied into the document' },
            );
            toast.error(message);
            return { success: false, error: message, code: error.code };
        }
    }

    /** Taslağı revizyon olarak yayınla (onay varsa bekler). */
    async publishDynamicReportDraft(documentId, body = {}) {
        try {
            const res = await this.service.publishDynamicReportDraft(documentId, body);
            toast.success(i18next.t('integrationToasts:revisionSaved', { defaultValue: 'Revision saved' }));
            return { success: true, data: res?.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchDocumentPartyTemplate(params = {}) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId) {
            return { success: false, data: null };
        }
        try {
            const response = await this.service.getPartyTemplate(params);
            return { success: response.success !== false, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, error: message };
        }
    }

    async fetchDocumentParty(documentId) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !documentId) {
            return { success: false, data: null, partyTemplate: null };
        }
        try {
            const response = await this.service.getParty(documentId);
            return {
                success: response.success !== false,
                data: response.data,
                partyTemplate: response.partyTemplate ?? null,
            };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, data: null, partyTemplate: null, error: message };
        }
    }

    async setDocumentApprovers(documentId, approvers) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !documentId) {
            return { success: false, error: 'Tenant context required' };
        }
        try {
            const response = await this.service.setApprovers(documentId, approvers);
            if (response?.success === false) {
                return { success: false, error: response.error };
            }
            toast.success(
                i18next.t('reportOperationsDocument:messages.approversSaved', {
                        defaultValue: 'Approver list updated',
                    }),
            );
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async setDocumentApprovalMode(documentId, mode) {
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !documentId) {
            return { success: false, error: 'Tenant context required' };
        }
        try {
            const response = await this.service.setApprovalMode(documentId, mode);
            if (response?.success === false) {
                return { success: false, error: response.error };
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async syncDocumentParty(documentId, payload, options = {}) {
        const { silentSuccess = false } = options;
        const tenantId = useTenantStore.getState().selectedTenantId;
        if (!tenantId || !documentId) {
            return { success: false, error: 'Tenant context required' };
        }
        try {
            const response = await this.service.syncParty(documentId, payload);
            if (response?.success === false) {
                return { success: false, error: response.error };
            }
            if (!silentSuccess) {
                toast.success(
                    i18next.t('reportOperationsDocument:ownership.saveSuccess', {
                            defaultValue: 'Ownership and roles saved',
                        }),
                );
            }
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async saveDesignerDraftFromReportingHost({ docId, reportName }) {
        const canonical = `sgrc-doc-${String(docId || '').toLowerCase()}.draft.repx`;
        if (!/^[a-f0-9]{24}$/i.test(String(docId || '')) || reportName !== canonical) {
            toast.error(
                i18next.t('reportOperationsDocument:designer.saveMissingContext', {
                    defaultValue: 'Document and report must be selected for design save',
                }),
            );
            return { success: false };
        }
        try {
            const markRes = await this.markDynamicReportDraft(docId);
            if (!markRes.success) return markRes;
            toast.success(
                i18next.t('reportOperationsDocument:designer.draftSaved', {
                    defaultValue: 'Draft saved',
                }),
            );
            return { success: true };
        } catch (error) {
            toast.error(
                getIntegrationErrorMessage(error) || i18next.t('reportOperationsDocument:designer.revisionFailed', {
                    defaultValue: 'Could not save draft',
                }),
            );
            return { success: false, error: error?.message };
        }
    }

    notifyBulkDeleteOutcome({ pendingCount = 0, immediateCount = 0 }) {
        if (pendingCount > 0) {
            toast.info(
                i18next.t('reportOperationsDocument:messages.bulkDeletePendingApproval', {
                    count: pendingCount,
                    defaultValue: `${pendingCount} delete requests submitted for approval`,
                }),
            );
        }
        if (immediateCount > 0) {
            toast.success(
                i18next.t('reportOperationsDocument:messages.bulkDeleteSuccess', {
                    count: immediateCount,
                    defaultValue: `${immediateCount} documents deleted`,
                }),
            );
        }
    }

    notifyPendingApprovalLoadFailed() {
        toast.error(
            i18next.t('reportOperationsDocument:table.pendingApproval.loadFailed', {
                defaultValue: 'Could not load pending approval record',
            }),
        );
    }

    notifyPendingApprovalNone() {
        toast.error(
            i18next.t('reportOperationsDocument:table.pendingApproval.none', {
                defaultValue: 'Pending approval record not found',
            }),
        );
    }

    notifyPendingApprovalNoUpdate() {
        toast.error(
            i18next.t('reportOperationsDocument:table.pendingApproval.noUpdatePending', {
                defaultValue: 'No pending update record',
            }),
        );
    }
}
